import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useEffect, useState } from "react";
import { getPractice } from "../../api";
import { getPlacesOfService } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { getUsers } from "../../api";
import { EditPractice } from "./EditPractice";
import { AddPracticeLogo } from "./AddPracticeLogo";
import React from "react";

export function ViewPractice({ refresh, onPracticeUpdate = () => {} }) {
  const [practices, setPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [logoModalShow, setLogoModalShow] = useState(false);
  const [selectedPracticeURL, setSelectedPracticeURL] = useState(null);
  const [placesOfService, setPlacesOfService] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPlacesOfService()
      .then((data) => {
        if (cancelled) return;
        setPlacesOfService(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setPlacesOfService([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    async function getPractices() {
      const token = sessionStorage.getItem("user");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const allPrac = await getPractice();
          const allUsers = await getUsers();
          const findUser = allUsers.find((user) => user._id === decoded._id);

          // Filter practices and sort them
          const filteredPrac = allPrac.filter((prac) =>
            findUser.practice.includes(prac.practiceNumber)
          );

          // Sort practices: holdingPractice first, then alphabetically by practiceName
          const sortedPractices = filteredPrac.sort((a, b) => {
            if (a.holdingPractice === "true" && b.holdingPractice !== "true")
              return -1;
            if (a.holdingPractice !== "true" && b.holdingPractice === "true")
              return 1;
            return a.practiceName.localeCompare(b.practiceName);
          });

          setPractices(sortedPractices);
        } finally {
          setLoading(false);
        }
      }
    }
    getPractices();
  }, [refresh]);

  const handleEditClick = (practiceNumber) => {
    setSelectedPractice(practiceNumber);
    setModalShow(true);
  };

  const handleLogoClick = (practiceId, practiceLogoURL) => {
    setSelectedPracticeId(practiceId);
    setSelectedPracticeURL(practiceLogoURL);
    setLogoModalShow(true);
  };

  return (
    <Container fluid className="pt-2 acc-list">
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading practices…</p>
        </div>
      ) : practices.length === 0 ? (
        <p className="text-center">No practices found.</p>
      ) : (
        practices.map((practice) => (
          <div className="acc-card" key={practice.practiceNumber}>
            <div className="acc-card-top">
              <h6 className="mb-0 text-primary fw-bold">
                {practice.practiceName}
              </h6>
              <div className="d-flex align-items-center gap-2">
                {practice.holdingPractice === "true" && (
                  <Badge bg="primary">Main</Badge>
                )}
                <Badge bg="secondary">
                  {practice.practiceType === "Medical"
                    ? "Medical"
                    : "Non-Medical"}
                </Badge>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Edit practice</Tooltip>}
                >
                  <Button
                    variant="link"
                    className="text-primary text-decoration-none p-0"
                    style={{ width: 24, height: 24 }}
                    onClick={() => handleEditClick(practice.practiceNumber)}
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            <div className="acc-card-body">
              <Row className="align-items-start">
                <Col xs="auto" className="d-flex flex-column align-items-start">
                  <div className="d-flex align-items-start gap-2">
                    {practice.logoURL ? (
                      <img src={practice.logoURL} alt="Practice logo" height={80} />
                    ) : (
                      <div
                        className="border rounded d-flex align-items-center justify-content-center text-muted"
                        style={{ width: 80, height: 80 }}
                      >
                        <i className="fa-solid fa-image"></i>
                      </div>
                    )}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Edit logo</Tooltip>}
                    >
                      <Button
                        variant="link"
                        className="text-primary text-decoration-none p-0"
                        style={{ width: 24, height: 24 }}
                        onClick={() =>
                          handleLogoClick(practice._id, practice.logoURL)
                        }
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </Button>
                    </OverlayTrigger>
                  </div>
                </Col>

                <Col className="d-flex flex-column justify-content-start">
                  <p className="mb-0 text-muted">
                    Practice Number: {practice.practiceNumber}
                  </p>
                  {practice.hpcsaNumber && (
                    <p className="mb-0 text-muted">
                      HPCSA: {practice.hpcsaNumber}
                    </p>
                  )}
                  {practice.vatNumber && (
                    <p className="mb-0 text-muted">VAT: {practice.vatNumber}</p>
                  )}
                  <p className="mb-1 text-muted">{practice.practiceCare}</p>
                </Col>
              </Row>

              {/* Locations grid (full width) */}
              {(() => {
                const locations =
                  Array.isArray(practice.locations) && practice.locations.length
                    ? practice.locations
                    : [
                        {
                          name: practice.primaryLocationName || "Main Location",
                          address1: practice.practiceAddress || "",
                          address2: practice.practiceAddress2 || "",
                          city: practice.practiceCity || "",
                          province: practice.practiceProvince || "",
                          postalCode: practice.practicePostalCode || "",
                          placeOfService: practice.practicePlaceOfService || "",
                        },
                      ];

                return (
                  <Row className="mt-2 gx-3">
                    {locations.map((loc, idx) => {
                      // For the primary slot (idx 0) fall back to the top-level
                      // practicePlaceOfService: older docs may have the POS only
                      // at the practice level, not yet mirrored into locations[0].
                      const posCode =
                        loc.placeOfService ||
                        (idx === 0 ? practice.practicePlaceOfService : "") ||
                        "";
                      const posMatch = posCode
                        ? placesOfService.find((p) => p.HCFA_code === posCode)
                        : null;
                      return (
                        <Col
                          key={`${loc.name || "loc"}-${idx}`}
                          xs={12}
                          sm={6}
                          md={3}
                          className="mb-2"
                        >
                          <div
                            className="mb-1 fw-semibold text-primary text-truncate"
                            title={loc.name}
                          >
                            <i className="fa-solid fa-location-dot me-2 text-primary" />
                            {loc.name || "Location"}
                          </div>
                          <div className="text-muted small text-wrap">
                            {loc.address1 && <div>{loc.address1}</div>}
                            {loc.address2 && <div>{loc.address2}</div>}
                            {(loc.city || loc.province || loc.postalCode) && (
                              <div>
                                {loc.city}
                                {loc.city && loc.province ? ", " : ""}
                                {loc.province} {loc.postalCode}
                              </div>
                            )}
                            {posCode && (
                              <div>
                                POS: {posCode}
                                {posMatch ? ` - ${posMatch.newDescription}` : ""}
                              </div>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                );
              })()}

              {/* Contact row (single, full width) */}
              <Row className="mt-2">
                <Col xs={12}>
                  <p className="mb-1">
                    <i className="fa-solid fa-phone me-2 text-primary"></i>
                    {practice.practicePhoneNumber}
                  </p>
                  <p className="mb-1">
                    <i className="fa-solid fa-envelope me-2 text-primary"></i>
                    {practice.practiceEmail}
                  </p>
                  {practice.practiceWebsite && (
                    <p className="mb-1">
                      <i className="fa-solid fa-globe me-2 text-primary"></i>
                      {practice.practiceWebsite}
                    </p>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        ))
      )}

      {modalShow && (
        <EditPractice
          show={modalShow}
          onHide={() => setModalShow(false)}
          practiceNum={selectedPractice}
          onPracticeUpdate={onPracticeUpdate}
        />
      )}
      {logoModalShow && (
        <AddPracticeLogo
          show={logoModalShow}
          onHide={() => setLogoModalShow(false)}
          practiceId={selectedPracticeId}
          practiceLogoURL={selectedPracticeURL}
          onLogoUpdate={onPracticeUpdate}
        />
      )}
    </Container>
  );
}
